/**
 * Sherpa SDK Integration Module
 *
 * This module manages loading and initializing the Sherpa SDK for travel document requirements.
 * It provides a Promise-based mechanism to detect when the SDK is fully loaded and ready to use.
 *
 * The module handles:
 * 1. Creating a Promise to track SDK loading state
 * 2. Setting up event handlers for SDK loading events
 * 3. Providing a method to dynamically load the SDK script
 * 4. Preventing duplicate script loading
 */

(function () {
  'use strict';

  const SHERPA_CONFIG = {
    scriptId: 'sherpa-sdk-script',
    sdkUrl: 'https://sdk.joinsherpa.io/widget.js?appId=spRiBTNFZZ',
  };

  // Create a Promise to track when the SDK is fully loaded
  // This allows components to wait for the SDK to be ready before using it
  window.sherpaSdkLoaded = new Promise((resolve, reject) => {
    window.resolveSherpaLoaded = resolve;

    // Set a timeout to reject the promise if loading takes too long
    setTimeout(() => {
      reject(new Error('Sherpa SDK loading timed out after 30 seconds'));
    }, 30000); // 30-second timeout
  });

  /**
   * Event handler required by the Sherpa SDK
   * This function receives events from the SDK when certain actions occur
   *
   * @param {Object} event - The event object from Sherpa SDK
   * @param {string} event.type - The type of event that occurred
   */
  let hasResolvedSdkLoaded = false;

  window.onSherpaEvent = function (event) {
    // Check if the event indicates the SDK has successfully loaded
    if (event.type === 'sdkLoaded') {
      if (!hasResolvedSdkLoaded) {
        hasResolvedSdkLoaded = true;

        if (window.resolveSherpaLoaded) {
          window.resolveSherpaLoaded(true);
        }
      }
    }
  };

  /**
   * Dynamically loads the Sherpa SDK script
   * Can be called conditionally based on feature flags
   * Ensures the script is loaded only once even if called multiple times
   *
   * @returns {Promise<boolean>} - Returns the SDK loading promise
   */
  function loadSherpaSDK() {
    // Return existing promise if loading has already been initiated
    if (hasResolvedSdkLoaded) {
      return Promise.resolve(true);
    }

    // Check if the script element already exists in the DOM
    if (document.getElementById(SHERPA_CONFIG.scriptId)) {
      return window.sherpaSdkLoaded;
    }

    const script = document.createElement('script');
    script.id = SHERPA_CONFIG.scriptId;
    script.src = SHERPA_CONFIG.sdkUrl;
    script.defer = true;

    script.onerror = function (error) {
      console.error('Failed to load Sherpa SDK:', error);
      if (window.resolveSherpaLoaded) {
        window.resolveSherpaLoaded(false);
      }
    };

    document.head.appendChild(script);

    return window.sherpaSdkLoaded;
  }

  // Expose the function globally for components that need it
  window.loadSherpaSDK = loadSherpaSDK;

  // Auto-load the SDK when the script runs
  // This ensures the SDK is loading as soon as the page loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadSherpaSDK);
  } else {
    // DOM is already loaded
    loadSherpaSDK();
  }
})();
